import React from "react";
import PropTypes from "prop-types";
//Material imports
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
// Limitless imports
import { useTranslation, useAppConfig } from "@limitless/common/hooks";
import { phoneNumberIsInternationalFormat } from "@limitless/portal/utils";
import { useHistory } from "react-router-dom";
import Link from "@mui/material/Link";
import LoginButton from "../buttons/login-button/LoginButton";
import { useMFASetupContext } from "../../hooks/mfa-setup/MFASetupContext";
import DialPrefixWithPhoneInput from "@limitless/modern/inputs/dial-prefix-with-phone-input/DialPrefixWithPhoneInput";
import { useLoginWrapperContext } from "../../../expert";
import { useExpertProfileContext } from "../../../expert";
const useStyles = makeStyles(theme => ({
  textInputStyle: {
    WebkitBoxShadow: `0 0 0 1000px ${theme.palette.contrast.light} inset`,
    backgroundColor: theme.palette.contrast.light,
    borderRadius: 4,
    marginBottom: 32,
    paddingLeft: 16,
    paddingRight: 16,
    maxWidth: 325
  },
  listboxStyle: {
    maxHeight: 120
  },
  paperStyle: {
    backgroundColor: theme.palette.contrast.light
  },
  autoCompleteRootStyle: {
    left: "-15px !important"
  },
  button: {
    width: 300
  },
  disclaimer: {
    marginTop: 24
  }
}));
const MFAPhoneSubmit = ({
  mfaState,
  setMfaState,
  dialCodes
}) => {
  const classes = useStyles();
  const {
    t
  } = useTranslation();
  const {
    submitPhoneNumber
  } = useMFASetupContext();
  const {
    mfaRequestCodeSuccessCB
  } = useLoginWrapperContext();
  const {
    profile
  } = useExpertProfileContext();
  const history = useHistory();
  const {
    getContactUsUrl
  } = useAppConfig();
  const contactUsClickHandler = () => {
    const navigationPath = "/contact-us";
    const url = getContactUsUrl(navigationPath);
    if (url === navigationPath) {
      history.push(url);
    } else {
      window.open(url, "_blank");
    }
  };
  const privacyPolicyClickHandler = () => {
    // Public per-locale Expert privacy policy (same document the sign-up flow links to),
    // served from the environment's public origin alongside the app.
    const locale = profile !== null && profile !== void 0 && profile.language && profile !== null && profile !== void 0 && profile.country ? `${profile.language}-${profile.country}` : "en-GB";
    window.open(`${window.location.origin}/content/${locale}/expert_web_app/terms/expert_privacy_policy.html`, "_blank");
  };
  const phoneIsValid = React.useMemo(() => phoneNumberIsInternationalFormat(mfaState.phoneNumber, dialCodes), [mfaState.phoneNumber]);
  const phoneSubmitHandler = async () => {
    setMfaState({
      ...mfaState,
      loading: true
    });
    mfaRequestCodeSuccessCB();
    submitPhoneNumber(mfaState, updatedState => setMfaState(updatedState), true);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DialPrefixWithPhoneInput, {
    name: "mfaPhoneNumber",
    autoComplete: "off",
    placeholder: "Your Phone Number",
    id: "mfaPhoneInputField",
    handlePhoneInputChange: (dialCodeValue, phoneNumberValue) => setMfaState({
      ...mfaState,
      phoneNumber: dialCodeValue + phoneNumberValue
    }),
    value: mfaState.phoneNumber.trim(),
    classes: {
      root: classes.textInputStyle
    },
    inputProps: {
      onFocus: e => {
        e.currentTarget.setSelectionRange(mfaState.phoneNumber.length, mfaState.phoneNumber.length);
      }
    },
    dropdownListStyle: classes.listboxStyle,
    dropdownPaperStyle: classes.paperStyle,
    dropdownContainerStyle: classes.autoCompleteRootStyle,
    disableCountrySelection: true
  }), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(LoginButton, {
    disabled: mfaState.submitPhoneScreen && !phoneIsValid || !mfaState.submitPhoneScreen,
    loading: mfaState.loading,
    id: "mfaSetupButton",
    onClick: phoneSubmitHandler,
    containerClasses: classes.button
  }, t("Request Code")), mfaState.errTxt && /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(Box, {
    minHeight: 38
  }), /*#__PURE__*/React.createElement(Typography, {
    color: "error",
    variant: "subtitle1"
  }, t(mfaState.errTxt)))), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "textSecondary",
    component: "p",
    className: classes.disclaimer
  }, t("By entering your phone number you are opting in to receive verification codes to confirm your Limitless account sign-ins. Codes will be sent via WhatsApp; if we are unable to deliver a code via WhatsApp, we will send it to you by SMS instead. You will receive a maximum of one message per sign-in attempt.")), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "textSecondary",
    component: "p",
    className: classes.disclaimer
  }, t("You can opt out by choosing not to proceed with your account registration, and you can opt out at any time by contacting us and requesting to close your Limitless account. If you wish to opt out or experience any issues with your codes, please"), " ", /*#__PURE__*/React.createElement(Link, {
    id: "mfaDisclaimerContactUs",
    style: {
      cursor: "pointer"
    },
    onClick: contactUsClickHandler
  }, t("contact us")), "."), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "textSecondary",
    component: "p",
    className: classes.disclaimer
  }, t("Data charges may apply to receive codes via WhatsApp depending on your plan with your provider. Carrier charges may apply to receive authentication codes by SMS depending on your plan with your Carrier, and if you reply to any verification SMS messages then Carrier charges may also apply. WhatsApp messages are subject to WhatsApp's own terms. Limitless, WhatsApp and Carriers are not liable for delayed or undelivered messages. If you have any questions regarding privacy, please see our"), " ", /*#__PURE__*/React.createElement(Link, {
    id: "mfaDisclaimerPrivacyPolicy",
    style: {
      cursor: "pointer"
    },
    onClick: privacyPolicyClickHandler
  }, t("Privacy Policy")), "."));
};
export default MFAPhoneSubmit;
MFAPhoneSubmit.propTypes = {
  mfaState: PropTypes.object,
  dialCodes: PropTypes.array,
  setMfaState: PropTypes.func
};